import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import { round2, formatTwoDecimals } from "../../global/money";

// Money columns of the ledger: rounded the standard way, two decimals.
const sumOf = (data, key) => round2(data.reduce((sum, i) => sum + (i[key] || 0), 0));

// ==========================================
//            Print Function PDF
// ==========================================
export const generateInvoiceLedgerPdf = async (data, modalDetails) => {
  const doc = new jsPDF({
    orientation: "landscape",
    format: "a4",
  });

  // =========================
  // 🔵 Initial Values
  // =========================
  const pageWidth = doc.internal.pageSize.getWidth();
  let x = 18;
  let y = 0;
  const companyName = modalDetails?.company?.companyName || "";
  const companyntn = modalDetails?.company?.ntn || "";
  const startDate = modalDetails?.startDate || "";
  const endDate = modalDetails?.endDate || "";

  // =========================
  // 🔵 Title Section
  // =========================
  doc.setFont("Helvetica", "bold");
  doc.setFontSize(16);
  const titleX = (pageWidth - doc.getTextWidth(companyName)) / 2;
  y = 15;
  doc.text(companyName, titleX, y);
  doc.setFontSize(11);
  const NTN = `NTN # ${companyntn}`;
  const companyX = (pageWidth - doc.getTextWidth(NTN)) / 2;
  y += 5;
  doc.text(NTN, companyX, y);

  // =========================
  // 🔵 Body Section
  // =========================
  y += 15;
  doc.setFontSize(12);
  // Start Date
  doc.setFont("Helvetica", "bold");
  doc.text("Start Date: ", x, y);
  doc.setFont("helvetica", "normal");
  doc.text(moment(startDate).format("DD-MM-YYYY"), doc.getTextWidth("Start Date:   ") + x, y);
  // End Date
  doc.setFont("helvetica", "normal");
  doc.text(moment(endDate).format("DD-MM-YYYY"), pageWidth - x * 2, y);
  doc.setFont("Helvetica", "bold");
  doc.text("End Date: ", pageWidth - (doc.getTextWidth(endDate) + x * 2), y);

  // =========================
  // 🔵 Tabel Section
  // =========================
  const tableBody =
    data?.length > 0 && Array.isArray(data)
      ? data.map((invoice, index) => [
          index + 1,
          invoice?.FBRInvoiceRefNo,
          invoice?.FBRInvoiceStatus,
          invoice?.buyerId?.businessName,
          moment(invoice?.date).format("DD-MM-YYYY"),
          formatTwoDecimals(invoice?.subTotal),
          formatTwoDecimals(invoice?.taxTotal),
          formatTwoDecimals(invoice?.grandTotal),
          invoice?.scenarioId?.name,
        ])
      : [];
  const totalSub = sumOf(data, "subTotal");
  const totalTax = sumOf(data, "taxTotal");
  const totalGrand = sumOf(data, "grandTotal");
  tableBody.push([
    {
      content: `TOTAL (${startDate ? moment(startDate).format("D MMM YYYY") : ""} - ${endDate ? moment(endDate).format("D MMM YYYY") : ""})`,
      colSpan: 5,
      styles: { halign: "center", fontStyle: "bold" },
    },
    { content: formatTwoDecimals(totalSub), styles: { fontStyle: "bold" } },
    { content: formatTwoDecimals(totalTax), styles: { fontStyle: "bold" } },
    { content: formatTwoDecimals(totalGrand), styles: { fontStyle: "bold" } },
    {
      content: "",
    },
  ]);
  autoTable(doc, {
    startY: y + 10,
    head: [
      [
        "Sr.",
        "FBR Invoice #",
        "FBR Invoice Status",
        "Buyer Name",
        "Date",
        "Sub Total",
        "Tax Total",
        "Grand Total",
        "Transaction Type",
      ],
    ],
    body: tableBody,
    theme: "grid",
    styles: { fontSize: 12, cellPadding: 3 },
    headStyles: {
      fillColor: [0, 102, 204],
      textColor: 255,
      halign: "center",
    },
  });

  // =========================
  // 🔵 FINAL OUTPUT
  // =========================
  const pdfBlob = doc.output("blob");
  const url = URL.createObjectURL(pdfBlob);
  window.open(url);
};

// ==========================================
//           Print Function Excel
// ==========================================
export const generateInvoiceLedgerExcel = async (data, modalDetails) => {
  const startDate = modalDetails?.startDate || "";
  const endDate = modalDetails?.endDate || "";
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Invoice Ledger");

  // =========================
  // 🔵 Header Row
  // =========================
  const headerRow = worksheet.addRow([
    "Sr.",
    "FBR Invoice #",
    "FBR Invoice Status",
    "Buyer Name",
    "Date",
    "Sub Total",
    "Tax Total",
    "Grand Total",
    "Transaction Type",
  ]);

  // 👉 Bold header
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: "center" };
  });

  // =========================
  // 🔵 Data Rows
  // =========================
  data.forEach((invoice, index) => {
    worksheet.addRow([
      index + 1,
      invoice?.FBRInvoiceRefNo,
      invoice?.FBRInvoiceStatus,
      invoice?.buyerId?.businessName,
      moment(invoice?.date).format("DD-MM-YYYY"),
      round2(invoice?.subTotal),
      round2(invoice?.taxTotal),
      round2(invoice?.grandTotal),
      invoice?.scenarioId?.name,
    ]);
  });

  // =========================
  // 🔵 Empty Row (spacing)
  // =========================
  worksheet.addRow([]);

  // =========================
  // 🔵 Total
  // =========================
  const totalSub = sumOf(data, "subTotal");
  const totalTax = sumOf(data, "taxTotal");
  const totalGrand = sumOf(data, "grandTotal");

  const totalRow = worksheet.addRow([
    `TOTAL (${startDate ? moment(startDate).format("D MMM YYYY") : ""} - ${endDate ? moment(endDate).format("D MMM YYYY") : ""})`,
    "",
    "",
    "",
    "",
    totalSub,
    totalTax,
    totalGrand,
    "",
  ]);

  const rowIndex = totalRow.number;

  // 👉 Merge first 5 columns (like PDF colSpan)
  worksheet.mergeCells(`A${rowIndex}:E${rowIndex}`);

  // 👉 Bold + center TOTAL row
  totalRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: "center" };
  });

  // =========================
  // 🔵 Column Widths
  // =========================
  worksheet.columns = [
    { width: 6 },
    { width: 20 },
    { width: 22 },
    { width: 28 },
    { width: 15 },
    { width: 15 },
    { width: 15 },
    { width: 18 },
    { width: 22 },
  ];
  // Sub Total, Tax Total and Grand Total always show two decimals.
  ["F", "G", "H"].forEach((column) => {
    worksheet.getColumn(column).eachCell((cell, rowNumber) => {
      if (rowNumber > 1) cell.numFmt = "0.00";
    });
  });

  // =========================
  // 🔵 Export File
  // =========================
  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    "Invoice_Ledger.xlsx",
  );
};
